/**
 * wet_layer — the weather on PROPS and STRUCTURES (shader gaps program round 3, weather surfaces, 2026-09-30).
 *
 * The ground darkened and glossed in the rain; every wall, crate, awning and hull stayed bone dry. This is the wet
 * look for the asset materials, composed INTO `material_finish.composeFinishLayers`' one graph (grime → WET → snow on
 * top; THE CLASSIC-KEY LAW keys it), driven by the ONE wet clock (`lib/wet_state.ts`) the ground reads too.
 *
 * Lagarde, "Water drop 3b — physically based wet surfaces" (2013), per fragment:
 *   factor    = lerp(1, 0.2, (1 − metal) · porosity)          — the darkest a soaked porous surface gets
 *   albedo   *= lerp(1, factor, absorbed)                      — ABSORBED water darkens (metal and glass never)
 *   roughness*= lerp(1, factor, 0.5 · absorbed)               — his gloss boost, in roughness terms
 *   roughness = lerp(roughness, water 0.06, film · 0.85)      — the SURFACE FILM smooths toward water
 * The film also takes ~8 % of the diffuse (the water's Fresnel over it). Metalness is PER TEXEL for a finished
 * (ORM-baked) material — its metal texels take the metal clock (film only), the rest the material's porous class.
 *
 * WHERE THE WEATHER REACHES: `level × exposure × facing`. The exposure is the roof map (`rain_shelter`) sampled in the
 * VERTEX stage and interpolated — the props are near their sampled-texture budget, a vertex-stage binding counts
 * against the vertex stage's own (three sets a texture's visibility per stage). The facing is the rain's: a surface
 * turned toward where the rain comes from (the wind's slant) fills fastest, a wall in still air a little (splash and
 * run-off), an underside never — `facingRain`. Absorbed water shows on walls too (a soaked wall is dark), a little
 * less than on a ledge.
 *
 * THE GATE: the weather is the Commons'; in space, a personal world or a station the layer draws nothing
 * (`uWetGate` 0 — mix(x, y, 0) is x exactly). The kill switch `atlas:wet = 'off'` and the flag off both build nothing.
 */
import * as THREE from 'three/webgpu';
import { uniform, float, vec3, mix, saturate, smoothstep, dot, normalWorldGeometry, positionWorld, varying } from 'three/tsl';
import { WET_PARAMS, wetClassForMaterial, type WetClass } from './wet_state.js';
import { shelterExposure } from './rain_shelter.js';
import { activePreset } from './quality_tiers.js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TSL graphs are loosely typed
type N = any;

export type WetSurfaces = 'off' | 'ground' | 'all';

/** The flag (`TierPreset.wetSurfaces`) with the kill switch applied. Unset = 'ground' (the clock, no prop layer). */
// PERF (shader sweep C, 2026-10-06): WeatherModule's driveWetClock asks this EVERY frame — the kill switch was a
// synchronous localStorage read per frame; it is read at 1 Hz now (the adaptive-quality / far-tier opt-out rule).
let wetKillAt = -Infinity, wetKilled = false;
export function wetSurfacesMode(): WetSurfaces {
  const now = typeof performance !== 'undefined' ? performance.now() : 0;
  if (now - wetKillAt >= 1000) {
    wetKillAt = now;
    try { wetKilled = localStorage.getItem('atlas:wet') === 'off'; } catch { wetKilled = false; /* no storage */ }
  }
  if (wetKilled) return 'off';
  const m = (activePreset() as { wetSurfaces?: WetSurfaces }).wetSurfaces;
  return m === 'off' || m === 'all' ? m : 'ground';
}

/** (stone absorbed, stone film, wood absorbed, wood film) */
export const uWetA = uniform(new THREE.Vector4(0, 0, 0, 0));
/** (fabric absorbed, fabric film, metal film, glass film) */
export const uWetB = uniform(new THREE.Vector4(0, 0, 0, 0));
/** 1 in the Commons outdoors, else 0 (the layer draws nothing). */
export const uWetGate = uniform(0);
/** The unit direction the rain comes FROM (up and upwind) — the facing test. */
export const uRainFrom = uniform(new THREE.Vector3(0, 1, 0));

export const wetLayerStats = { built: 0, byClass: { stone: 0, wood: 0, fabric: 0, metal: 0, glass: 0, soil: 0 } as Record<WetClass, number>, lastClass: '' as string };

/** The rain's facing weight of a surface normal n (0 underside … 1 facing the rain), CPU mirror of the TSL. */
export function facingRainCpu(ny: number, dotFrom: number): number {
  const f = Math.min(1, Math.max(0, dotFrom * 0.75 + 0.25));
  return ny < -0.35 ? 0 : f;
}

export interface WetHandle {
  cls: WetClass;
  /** albedo rgb → wet albedo rgb */
  color: (rgb: N) => N;
  /** roughness → wet roughness */
  rough: (r: N) => N;
}

/**
 * The wet nodes for one asset material, or null when the layer is off / the material is not a surface that wets
 * (transparent, alpha-tested foliage cards). `metal` is the fragment's metalness node (per texel for a finished
 * material); `finished` says the scalar metalness is not the class.
 */
export function wetLayerFor(material: THREE.Material, metal: N, finished: boolean): WetHandle | null {
  if (wetSurfacesMode() !== 'all') return null;
  const m = material as THREE.MeshStandardMaterial;
  if (m.transparent || (m.alphaTest ?? 0) > 0) return null;
  const cls = wetClassForMaterial(m.name ?? '', m.metalness ?? 0, finished);
  const P = WET_PARAMS[cls];
  wetLayerStats.built++; wetLayerStats.byClass[cls]++; wetLayerStats.lastClass = cls;
  // the class's own clock (porous classes read uWetA / uWetB; metal and glass have no absorbed water)
  const a: N = uWetA, b: N = uWetB;
  const absorbedCls: N = cls === 'stone' ? a.x : cls === 'wood' ? a.z : cls === 'fabric' ? b.x : float(0);
  const filmCls: N = cls === 'stone' ? a.y : cls === 'wood' ? a.w : cls === 'fabric' ? b.y : cls === 'metal' ? b.z : b.w;
  // metal texels (a finished material's ORM) take the metal clock: film only, no darkening
  const metalW: N = cls === 'metal' ? float(1) : cls === 'glass' ? float(0) : smoothstep(float(0.35), float(0.8), saturate(metal));
  const porosity = P.porosity;
  const exposure: N = activePreset().rainShelter ? varying(shelterExposure(positionWorld as never) as never, 'vWetExposure') : float(1);
  const n: N = normalWorldGeometry;
  const facing: N = saturate(dot(n, uRainFrom).mul(0.75).add(0.25)).mul(smoothstep(float(-0.5), float(-0.2), n.y));
  const up: N = saturate(n.y);
  const gate: N = uWetGate;
  const absorbed: N = absorbedCls.mul(float(1).sub(metalW)).mul(exposure).mul(mix(float(0.6), float(1), up)).mul(gate);
  const film: N = mix(filmCls, b.z, metalW).mul(exposure).mul(facing).mul(gate);
  const factor: N = mix(float(1), float(0.2), float(porosity).mul(float(1).sub(metalW)));
  return {
    cls,
    color: (rgb: N) => rgb.mul(mix(float(1), factor, absorbed)).mul(float(1).sub(film.mul(0.08))),
    rough: (r: N) => mix(r.mul(mix(float(1), factor, absorbed.mul(0.5))), float(0.06), film.mul(0.85)),
  };
}
